import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

/**
 * SuccessPage Component
 * 
 * This page is displayed after successful payment completion.
 * It handles two scenarios:
 * 
 * 1. EXTERNAL REDIRECT FROM PAYMENT GATEWAY (NIC USA):
 *    URL: https://yourdomain.com/?paymentResult=success&token={token}
 * 
 * 2. INTERNAL NAVIGATION:
 *    - When navigating from within the app, case info is passed via location.state
 * 
 * The component automatically:
 * - Detects the source (external redirect vs internal navigation)
 * - Calls /payment/confirm to create the case after payment verification
 * - Displays case information and success message
 * - Shows save status (saving/saved/error)
 */
const SuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if this is a redirect from payment gateway (query params) or internal navigation (state)
  const searchParams = new URLSearchParams(location.search);
  const tokenFromUrl = searchParams.get('token');
  
  const caseInfo = location.state?.caseInfo;
  const message = location.state?.message || <strong>You are still RESPONSIBLE for appearing at your next court date unless otherwise directed by the Court or your attorney.</strong>;
  const [saveStatus, setSaveStatus] = useState('saving'); // 'saving', 'saved', 'error'
  const [saveError, setSaveError] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    const saveCaseToDatabase = async () => {
      if (hasStartedRef.current) {
        return;
      }
      hasStartedRef.current = true;

      let storedData = null;
      try {
        const stored = sessionStorage.getItem('dui_payment_data');
        if (stored) {
          storedData = JSON.parse(stored);
        }
      } catch {
        sessionStorage.removeItem('dui_payment_data');
      }

      const paymentData = caseInfo || storedData;

      if (!paymentData) {
        setSaveStatus('error');
        setSaveError('No payment information available. Please contact support.');
        return;
      }

      try {
        // Build ConfirmPaymentModel matching backend expectation
        const rawPayload = {
          role: paymentData.role,
          firstName: paymentData.firstName,
          middleName: paymentData.middleName,
          lastName: paymentData.lastName,
          dateOfBirth: paymentData.dateOfBirth,
          email: paymentData.email,
          jailId: paymentData.jailId,
          policeReportNumber: paymentData.policeReportNumber,
          fileNumber: paymentData.fileNumber,
          countyOfArrest: paymentData.countyOfArrest,
          localReferenceId: paymentData.localRef || paymentData.localReferenceId,
          uniqueTransactionId: paymentData.uniqueTransId || paymentData.uniqueTransactionId,
          paymentReservationToken: tokenFromUrl || paymentData.token,
        };

        const nullableOptionalFields = new Set([
          'middleName',
          'jailId',
          'policeReportNumber',
          'fileNumber',
          'countyOfArrest',
        ]);

        const confirmPayload = Object.entries(rawPayload).reduce((acc, [key, value]) => {
          if (typeof value === 'string') {
            const trimmedValue = value.trim();

            if (!trimmedValue) {
              if (nullableOptionalFields.has(key)) {
                acc[key] = null;
              }
              return acc;
            }

            acc[key] = trimmedValue;
            return acc;
          }

          if (value === null || value === undefined) {
            if (nullableOptionalFields.has(key)) {
              acc[key] = null;
            }
            return acc;
          }

          acc[key] = value;
          return acc;
        }, {});

        const response = await fetch(API_ENDPOINTS.SUBMIT_CASE, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(confirmPayload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Failed to confirm payment');
        }

        const newCaseNumber = await response.text();

        setCaseData({
          ...paymentData,
          caseNumber: newCaseNumber,
          token: confirmPayload.paymentReservationToken,
        });
        setSaveStatus('saved');

        sessionStorage.removeItem('dui_payment_data');
      } catch (error) {
        setSaveStatus('error');
        setSaveError(error.message);
      }
    };

    saveCaseToDatabase();
  }, [caseInfo, tokenFromUrl]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleCheckStatus = () => {
    navigate('/status', {
      state: { caseNumber: caseData?.caseNumber || caseInfo?.caseNumber || '' }
    });
  };

  const paymentDate = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <div className="success-page">
      <div className="success-container" style={{
        maxWidth: '800px',
        margin: '2rem auto',
        padding: '2rem',
        backgroundColor: '#d4edda',
        border: '1px solid #c3e6cb',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        {/* Database save status indicator */}
        {saveStatus === 'saving' && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            padding: '0.75rem',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            color: '#856404'
          }}>
            ⏳ Saving case information to database...
          </div>
        )}
        {saveStatus === 'error' && (
          <div style={{
            backgroundColor: '#ecdedf',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            padding: '0.75rem',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            color: '#721c24'
          }}>
            ⚠️ Warning: Case data may not have been saved. Error: {saveError}
          </div>
        )}
        {saveStatus === 'saved' && (
          <div style={{
            backgroundColor: '#e8edee',
            border: '1px solid #bee5eb',
            borderRadius: '4px',
            padding: '0.75rem',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            color: '#0c5460'
          }}>
            ✅ Case information successfully saved
          </div>
        )}
        
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
        <h1 style={{ color: '#155724', marginBottom: '1rem' }}>Payment Successful!</h1>
        <p style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>{message}</p>

        <div className="success-info-grid">
          {(caseInfo || caseData) && (
            <div className="case-summary success-section-card" style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              padding: '1.5rem',
              textAlign: 'left'
            }}>
              <h3 style={{ color: '#495057', marginBottom: '1rem' }}>📋 Case Summary</h3>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {(caseInfo?.caseNumber || caseData?.caseNumber) && (
                  <p><strong>Tracking Number:</strong> {caseInfo?.caseNumber || caseData?.caseNumber}</p>
                )}
                <p> <strong>First Name:</strong> {caseInfo?.firstName || caseData?.firstName}</p>
                <p><strong>Last Name:</strong> {caseInfo?.lastName || caseData?.lastName}</p>
                <p><strong>Payment Amount:</strong> $107.00</p>
                <p><strong>Payment Date:</strong> {paymentDate}</p>
                <p><strong>Status:</strong> <span style={{ color: '#28a745', fontWeight: 'bold' }}>Pending Review</span></p>
                {(caseInfo?.token || caseData?.token || tokenFromUrl) && (
                  <p><strong>Transaction ID:</strong> {(caseInfo?.token || caseData?.token || tokenFromUrl)?.substring(0, 8)}...</p>
                )}
              </div>
            </div>
          )}

          <div className="important-info success-section-card" style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #fdf0c4',
            borderRadius: '4px',
            padding: '1rem',
            textAlign: 'left'
          }}>
            <h4 style={{ color: '#856404', marginBottom: '0.5rem' }}>📝 Important Information</h4>
            <ul style={{ color: '#856404', margin: 0, paddingLeft: '1.5rem' }}>
              <li>Please save your Tracking Number for future reference</li>
              <li>You will receive a confirmation email shortly</li>
              <li>Your case has been created and is now pending review</li>
              <li>Check your case status anytime using your Tracking Number</li>
            </ul>
          </div>
        </div>

        <div className="next-steps" style={{
          backgroundColor: '#e2e3e5',
          border: '1px solid #d6d8db',
          borderRadius: '4px',
          padding: '1rem',
          margin: '1.5rem 0',
          textAlign: 'left'
        }}>
          <h4 style={{ color: '#383d41', marginBottom: '0.5rem' }}>🔄 Next Steps</h4>
          <ol style={{ color: '#383d41', margin: 0, paddingLeft: '1.5rem' }}>
            <li>Wait for email confirmation</li>
            <li>Keep your Tracking Number available</li>
            <li>Check your case status periodically</li>
            <li>Contact support if you have questions</li>
          </ol>
        </div>

        <div className="action-buttons" style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginTop: '2rem'
        }}>
          <button 
            onClick={handleCheckStatus}
            style={{
              backgroundColor: '#007cba',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            📊 Check Case Status
          </button>
          <button 
            onClick={handleGoHome}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            🏠 Return to Home
          </button>
        </div>

        <div className="support-info" style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          fontSize: '0.9rem',
          color: '#6c757d'
        }}>
          <p><strong>Need Help?</strong></p>
          <p>Contact the program office at DUIProgram@coj.net or call (904) 630-1200</p>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;